'use client';

import { useQuery } from '@tanstack/react-query';

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  /**
   * Published article count, absent while the fallback list is in use.
   * Only a definitive zero disables the entry; unknown counts stay clickable.
   */
  articleCount?: number;
}

// Fallback categories rendered instantly while the live list is fetched from
// /api/blog/categories, so the dropdown never appears empty.
const FALLBACK_CATEGORIES: BlogCategory[] = [
  { id: 'indonesian-legal-guide', name: 'Indonesian Legal Guide', slug: 'indonesian-legal-guide', description: null },
  { id: 'pertanyaan-hukum', name: 'Pertanyaan Hukum', slug: 'pertanyaan-hukum', description: null },
  { id: 'justisio-product-update', name: 'Justisio Product Update', slug: 'justisio-product-update', description: null },
  { id: 'legal-updates', name: 'Legal Updates', slug: 'legal-updates', description: null },
  { id: 'opini-hukum', name: 'Opini Hukum', slug: 'opini-hukum', description: null },
];

// Provides the blog categories shown in the Kajian dropdown. The fallback list
// doubles as initial data, so the dropdown renders immediately and stays
// populated if the request fails.
export function useBlogCategories() {
  return useQuery<{ data: BlogCategory[] }>({
    queryKey: ['blog-categories'],
    queryFn: async () => {
      const res = await fetch('/api/blog/categories');
      if (!res.ok) throw new Error('Failed to fetch blog categories');
      return res.json();
    },
    initialData: { data: FALLBACK_CATEGORIES },
    staleTime: 1000 * 60 * 5,
    retry: 3,
  });
}
